import React, { FC, useMemo, useState } from 'react';
import { Pagination } from 'antd';
import { PassportsTransactionData } from '@/types';
import { timeConverter } from '@/utils/helpers';
import { Icon, faLocationDot } from '@/ui/components/Icon';

export const LEDGER_PAGE_SIZE = 10;
export const LEDGER_DATE_FORMAT = 'DD MMM YYYY HH:mm';

export const hasPosition = (t: PassportsTransactionData) =>
  t.latitude != null && t.longitude != null;

export const sortByDate = (
  list: PassportsTransactionData[],
  newestFirst = true,
) =>
  [...list].sort(
    (a, b) =>
      (newestFirst ? -1 : 1) *
      ((a.transDate || '').localeCompare(b.transDate || '') ||
        a.vehicleTransIndex - b.vehicleTransIndex),
  );

interface TransactionLedgerProps {
  title?: string;
  /** Visit view: short dates and no site column, as the visit is one site. */
  compact?: boolean;
  transactions: PassportsTransactionData[];
  total: number;
  onShowPosition: (transaction: PassportsTransactionData) => void;
}

export const TransactionLedger: FC<TransactionLedgerProps> = ({
  title = 'All transactions',
  compact,
  transactions,
  total,
  onShowPosition,
}) => {
  const [page, setPage] = useState(1);
  const [newestFirst, setNewestFirst] = useState(true);
  const sorted = useMemo(
    () => sortByDate(transactions, newestFirst),
    [transactions, newestFirst],
  );
  const start = (page - 1) * LEDGER_PAGE_SIZE;
  const rows = sorted.slice(start, start + LEDGER_PAGE_SIZE);
  const count = transactions.length;

  return (
    <div className="pb-ledger-wrap">
      <h2 className="pb-heading">{title}</h2>
      <p className="pb-sub">
        {count === total ? count : `${count} of ${total}`} recorded events •{' '}
        {LEDGER_PAGE_SIZE} per page • {newestFirst ? 'Newest' : 'Oldest'} first
      </p>
      {/* Keyed so each page and sort order starts scrolled to the top. */}
      <div className="pb-ledger-scroll" key={`${page}|${newestFirst}`}>
        <table className="pb-ledger">
          <thead>
            <tr>
              <th aria-sort={newestFirst ? 'descending' : 'ascending'}>
                <button
                  type="button"
                  onClick={() => {
                    setNewestFirst(!newestFirst);
                    setPage(1);
                  }}
                >
                  {compact ? 'Date / time' : 'Date & time'}{' '}
                  {newestFirst ? '↓' : '↑'}
                </button>
              </th>
              <th className="pb-ledger-event">
                {compact ? 'Event' : 'Transaction'}
              </th>
              {!compact && <th>Site</th>}
              <th className="text-end">Code</th>
              <th>
                <span className="visually-hidden">Recorded position</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.vehicleTransIndex}>
                <td className="pb-ledger-date">
                  {timeConverter(
                    t.transDate,
                    compact ? 'DD MMM HH:mm' : LEDGER_DATE_FORMAT,
                  )}
                </td>
                <td className="pb-ledger-event">{t.transDescription}</td>
                {!compact && (
                  <td className="pb-ledger-site">{t.locationDesc}</td>
                )}
                <td className="pb-ledger-code">{t.transType}</td>
                <td>
                  {hasPosition(t) && (
                    <button
                      type="button"
                      className="pb-map-action"
                      aria-label={`Show recorded position for ${t.transDescription}`}
                      onClick={() => onShowPosition(t)}
                    >
                      <Icon icon={faLocationDot} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!count && <p className="pb-empty">No transactions match.</p>}
      </div>
      <div className="pb-ledger-foot">
        <span>
          {count
            ? `Showing ${start + 1}-${start + rows.length} of ${count}`
            : 'Showing 0 of 0'}
        </span>
        {count > 0 && (
          <Pagination
            size="small"
            current={page}
            pageSize={LEDGER_PAGE_SIZE}
            total={count}
            showSizeChanger={false}
            onChange={setPage}
          />
        )}
      </div>
    </div>
  );
};
